import ip from "ip";

export enum Rank {
  User = 2,
  Signer = 128,
  Admin = 256,
  SuperAdmin = 512,
}

export interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  profiles: string[];
  rank: Rank;
}

export interface RankMapping {
  group?: string;
  name: string;
}

export interface Identity {
  userId: string;
  providerName: string;
  providerType: string;
  issuer: string;
  primary: boolean;
  dateCreated: number;
}

/**
 * This type represents a validator to check whether a restriction should be displayed based on the current
 * user's @see EffectiveProfile and @see Rank.
 */
export type RestrictionValidator = (signedInUserRank: Rank, effectiveProfile: EffectiveProfile) => boolean;

/**
 * This type represents a generator to create a restriction message based on the @see EffectiveProfile.
 */
export type MessageGenerator = (effectiveProfile: EffectiveProfile) => string;

/**
 * This type represents the effective profile configuration
 * for a user
 *
 * This is based on all the profiles which are allocated to the user,
 * with the most permissive profile taking precedence.
 */
export type EffectiveProfileProps = {
  private?: ProfileConfiguration,
  public?: ProfileConfiguration
}

export interface ProfileListItem {
  id: string
  name: string
}

/**
 * Represents the configuration which makes up the profile.
 */
export interface ProfileConfiguration {
  maxDuration: number,
  maxIpAddresses: number
}

/**
 * Represents an Effective Profile for the current user.
 *
 * Provides logic for validating Signing requests against
 * the current user's
 */
export class EffectiveProfile {
  private props: EffectiveProfileProps;
  static defaultProfileConfiguration: ProfileConfiguration = {
    maxIpAddresses: 0,
    maxDuration: 0
  }

  private constructor(effectiveProfileProps: EffectiveProfileProps) {
    this.props = effectiveProfileProps;

  }

  /**
   * Factory method to create an instance of an EffectiveProfile
   * @param effectiveProfileProps The attributes which make up an Effective Profile.
   */
  public static createEffectiveProfile(effectiveProfileProps: EffectiveProfileProps) {
    return new EffectiveProfile(effectiveProfileProps);
  }

  /**
   * Gets the max duration for the network the ipAddress belongs too.
   * @param ipAddress The IP address to return the duration for.
   * @returns The duration in seconds, or undefined if network is not covered
   */
  public durationForAddress(ipAddress: string): number|undefined {
    const config = ip.isPrivate(ipAddress) ? this.props?.private : this.props?.public;
    return config?.maxDuration ?? undefined;
  }

  /**
   * Checks to see whether the specified IP address is covered by one of the
   * networks defined in Effective Profile
   * @param ipAddress The IP address to check.
   * @returns A validation error, an array containing the value and the error message or null
   * if there is no validation failure.
   */
  public includesAddress(ipAddress: string) : [string, string] | null {
    if (ip.isPrivate(ipAddress) && !this.props?.private) {
      return [ipAddress, "Your effective profile does not include private networks"];
    }

    if (!ip.isPrivate(ipAddress) && !this.props?.public) {
      return [ipAddress, "Your effective profile does not include public networks"];
    }
    return null;
  }

  /**
   * Gets the maximum number of private IP addresses that can be specified in the signing request
   */
  public maxPrivateIpAddresses(): number {
    return this.privateConfiguration().maxIpAddresses;
  }

  /**
   * Gets the maximum number of public IP addresses that can be specified in the signing request
   */
  public maxPublicIpAddresses(): number {
    return this.publicConfiguration().maxIpAddresses;
  }

  /**
   * Gets the private @see ProfileConfiguration and returns a default if it has not been set.
   */
  public privateConfiguration(): ProfileConfiguration {
    return this.props?.private ?? EffectiveProfile.defaultProfileConfiguration;
  }

  /**
   * Gets the public @see ProfileConfiguration and returns a default if it has not been set.
   */
  public publicConfiguration(): ProfileConfiguration {
    return this.props?.public ?? EffectiveProfile.defaultProfileConfiguration;
  }
}
